import { Col, Text } from 'components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableComponents } from 'screens/Admin/components';
import { formatAmount } from './helpers';

const COUNTRY_LABEL_KEYS = {
  FRA: 'countries.france',
  DEU: 'countries.germany',
  JPN: 'countries.japan',
  MKD: 'countries.northMacedonia',
  POL: 'countries.poland',
  ZAF: 'countries.southAfrica',
  SWE: 'countries.sweden',
  GBR: 'countries.unitedKingdom',
  USA: 'countries.unitedStates'
};

const MrrShell = ({ data, viewMode = 'country' }) => {
  const { t } = useTranslation();
  const currencies = data?.currencies || [];
  const isPlanView = viewMode === 'plan';
  const isTeamView = viewMode === 'team';

  const rows = useMemo(() => {
    return currencies.flatMap((currencyBlock) => {
      const sourceRows = isPlanView
        ? currencyBlock.byPlan || []
        : isTeamView
          ? currencyBlock.byTeam || []
          : currencyBlock.byCountry || [];
      return sourceRows.map((row) => ({
        ...row,
        currency: currencyBlock.currency
      }));
    });
  }, [currencies, isPlanView, isTeamView]);

  const getCountryLabel = (countryCode) => {
    const key = COUNTRY_LABEL_KEYS[countryCode];
    if (!key) return countryCode || 'Unknown';
    return t(key);
  };

  if (!rows.length) {
    return (
      <Col wid="100%" marg="80px 0" centerAll noFlex>
        <Text className="text-[13px] text-[#97A3B5]">
          {t('subscriptions.admin.overview.mrr.empty')}
        </Text>
      </Col>
    );
  }

  return (
    <TableComponents.List
      loading={false}
      Header={() => (
        <>
          {isPlanView ? (
            <>
              <TableComponents.Th first>
                <Text>{t('subscriptions.admin.overview.mrr.plan')}</Text>
              </TableComponents.Th>
              <TableComponents.Th>
                <Text>{t('subscriptions.admin.overview.mrr.team')}</Text>
              </TableComponents.Th>
            </>
          ) : isTeamView ? (
            <TableComponents.Th first>
              <Text>{t('subscriptions.admin.overview.mrr.team')}</Text>
            </TableComponents.Th>
          ) : (
            <TableComponents.Th first>
              <Text>{t('subscriptions.admin.overview.mrr.country')}</Text>
            </TableComponents.Th>
          )}
          {!isTeamView && <TableComponents.Th>
            <Text>{t('subscriptions.admin.overview.mrr.countryCode')}</Text>
          </TableComponents.Th>}
          <TableComponents.Th>
            <Text>{t('subscriptions.admin.overview.mrr.currency')}</Text>
          </TableComponents.Th>
          <TableComponents.Th>
            <Text>{t('subscriptions.admin.overview.mrr.totalMrr')}</Text>
          </TableComponents.Th>
          <TableComponents.Th last>
            <Text>{t('subscriptions.admin.overview.mrr.subscribers')}</Text>
          </TableComponents.Th>
        </>
      )}
      Items={() =>
        rows.map((row) => (
          <TableComponents.Tr
            key={
              isPlanView
                ? `${row.currency}-${row.planId || row.title}-${row.teamId || row.teamName}`
                : isTeamView
                  ? `${row.currency}-${row.teamId || row.teamName}`
                  : `${row.currency}-${row.countryCode}`
            }
          >
            {isPlanView ? (
              <>
                <TableComponents.Td first>
                  <Text>{row.title || '-'}</Text>
                </TableComponents.Td>
                <TableComponents.Td>
                  <Text>{row.teamName || '-'}</Text>
                </TableComponents.Td>
              </>
            ) : isTeamView ? (
              <TableComponents.Td first>
                <Text>{row.teamName || '-'}</Text>
              </TableComponents.Td>
            ) : (
              <TableComponents.Td first>
                <Text>{getCountryLabel(row.countryCode)}</Text>
              </TableComponents.Td>
            )}
            {!isTeamView && <TableComponents.Td>
              <Text style={{ textTransform: 'uppercase' }}>{row.countryCode || '-'}</Text>
            </TableComponents.Td>}
            <TableComponents.Td>
              <Text>{row.currency || '-'}</Text>
            </TableComponents.Td>
            <TableComponents.Td>
              <Text>{formatAmount(row.total, row.currency)}</Text>
            </TableComponents.Td>
            <TableComponents.Td last>
              <Text>{row.subscribers || 0}</Text>
            </TableComponents.Td>
          </TableComponents.Tr>
        ))
      }
    />
  );
};

export default MrrShell;
